import React, { useMemo } from "react";
import { Box, Card, CardContent, Typography, Stack, Chip } from "@mui/material";
import { TrendingUp, TrendingDown, TrendingFlat } from "@mui/icons-material";

/**
 * Three-up KPI strip above the distribution chart. Always shows the primary
 * total; the comparison cards switch to a "no data" treatment when their
 * block is null (e.g. `all_time` has no comparable window, `period=year`
 * uses `yoy` only and `previous` is null on purpose).
 *
 * Distribution has both `comparisons.previous` and `comparisons.yoy` so we
 * can render both deltas at once — the bar chart's compare control is for
 * choosing which one to *visualize* in the bars, not which one to *answer*.
 *
 * @param {Object} props
 * @param {Object | null} props.data — full /distribution response
 * @param {boolean} props.loading
 */
const CancellationKpiCards = ({ data, loading }) => {
  const cards = useMemo(() => {
    if (!data) return null;
    const previous = data.comparisons?.previous ?? null;
    const yoy = data.comparisons?.yoy ?? null;

    return {
      primary: {
        label: formatPeriodLabel(data.period),
        value: data.total,
      },
      previous: buildDeltaCard("vs Previous Period", data.total, previous, "Previous data not available"),
      yoy: buildDeltaCard("vs Same Period Last Year", data.total, yoy, "No data for this period last year"),
    };
  }, [data]);

  if (loading && !data) {
    return (
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 3 }}>
        {[0, 1, 2].map((i) => (
          <Card key={i} sx={{ flex: 1, minHeight: 120, opacity: 0.5 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Loading…</Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  }
  if (!cards) return null;

  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 3 }}>
      <KpiCard
        label="Total"
        primaryValue={cards.primary.value.toLocaleString()}
        secondary={cards.primary.label}
        accentColor="primary.main"
      />
      <KpiCard
        label={cards.previous.label}
        primaryValue={cards.previous.primaryValue}
        secondary={cards.previous.secondary}
        deltaChip={cards.previous.deltaChip}
        muted={cards.previous.muted}
      />
      <KpiCard
        label={cards.yoy.label}
        primaryValue={cards.yoy.primaryValue}
        secondary={cards.yoy.secondary}
        deltaChip={cards.yoy.deltaChip}
        muted={cards.yoy.muted}
      />
    </Stack>
  );
};

const KpiCard = ({ label, primaryValue, secondary, deltaChip, accentColor, muted }) => (
  <Card sx={{ flex: 1, minHeight: 120, opacity: muted ? 0.6 : 1 }}>
    <CardContent>
      <Typography variant="overline" color="text.secondary" sx={{ display: "block", letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mt: 0.5 }}>
        <Typography variant="h4" sx={{ color: accentColor, fontWeight: 600, lineHeight: 1.2 }}>
          {primaryValue}
        </Typography>
        {deltaChip}
      </Stack>
      {secondary && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {secondary}
        </Typography>
      )}
    </CardContent>
  </Card>
);

/**
 * Build a comparison card payload. When `comparisonBlock` is null the card
 * renders in its "no data" state — matches Lucas's brief: still show it,
 * just dimmed with an explanation.
 */
const buildDeltaCard = (label, currentTotal, comparisonBlock, noDataExplanation) => {
  if (!comparisonBlock) {
    return {
      label,
      primaryValue: "—",
      secondary: noDataExplanation,
      deltaChip: null,
      muted: true,
    };
  }
  const previousTotal = comparisonBlock.total;
  const periodLabel = formatPeriodLabel(comparisonBlock.period);

  let deltaChip = null;
  if (previousTotal === 0 && currentTotal === 0) {
    deltaChip = <Chip size="small" icon={<TrendingFlat fontSize="small" />} label="—" />;
  } else if (previousTotal === 0) {
    deltaChip = <Chip size="small" icon={<TrendingUp fontSize="small" />} label="NEW" color="info" />;
  } else {
    const deltaPct = Math.round(((currentTotal - previousTotal) / previousTotal) * 100);
    // For cancellations, going DOWN is good (fewer churns) — so the color
    // semantics are inverted from a typical revenue chart.
    const colorByDelta = deltaPct > 0 ? "warning" : deltaPct < 0 ? "success" : "default";
    const Icon = deltaPct > 0 ? TrendingUp : deltaPct < 0 ? TrendingDown : TrendingFlat;
    deltaChip = (
      <Chip
        size="small"
        icon={<Icon fontSize="small" />}
        color={colorByDelta}
        label={`${deltaPct > 0 ? "+" : ""}${deltaPct}%`}
      />
    );
  }

  return {
    label,
    primaryValue: previousTotal.toLocaleString(),
    secondary: periodLabel,
    deltaChip,
    muted: false,
  };
};

/**
 * Pretty-print a period block's window. Falls back to the key when from/to
 * aren't set (`all_time`).
 *
 * All formatters anchor to UTC because the API timestamps come from
 * Carbon's UTC-anchored window boundaries — without `timeZone: "UTC"` the
 * locale formatter would interpret the parsed Date in the user's local
 * zone, which for any negative-UTC offset (EDT/PDT) makes UTC midnight on
 * the first of the month roll back to the previous calendar day, producing
 * an "April → March" off-by-one in the card labels.
 */
const formatPeriodLabel = (period) => {
  if (!period) return "";
  if (!period.from || !period.to) {
    return period.key === "all_time" ? "All time" : period.key.replace(/_/g, " ");
  }
  const from = new Date(period.from);
  const to = new Date(period.to);
  const sameMonth = from.getUTCFullYear() === to.getUTCFullYear() && from.getUTCMonth() === to.getUTCMonth();
  if (sameMonth) {
    return from.toLocaleString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
  }
  return `${from.toLocaleDateString(undefined, { timeZone: "UTC" })} – ${to.toLocaleDateString(undefined, { timeZone: "UTC" })}`;
};

export default CancellationKpiCards;
